import {
  BMComponentsType,
  BMElevationType,
  BMPrimitives,
  useBMTheme,
} from "@traveloka/district-bloom";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Animated,
  FlatList,
  LayoutChangeEvent,
  Platform,
  Pressable,
  StyleSheet,
} from "react-native";

import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";
import { ErrorSection } from "../../Error";
import {
  MAX_HEIGHT_OPTIONS_CONTAINER,
  useAnimatedDropdownHeight,
  useGetValue,
} from "../hooks";
import {
  BMDropdownDesktopOptionsProps,
  BMDropdownOptionsValue,
} from "../types";

import { appendTestId } from "../../../utils";
import DotLoader from "../../DotLoader";
import BMView from "../../View/BMView";
import BMDropdownNotFoundOptions from "./BMDropdownNotFoundOptions";
import BMDropdownOptionItem from "./BMDropdownOptionItem";

export default function BMDropdownDesktopOptions<T>({
  show,
  query,
  option,
  isRadio,
  selected,
  wrapperRef,
  wrapperStyles,
  searchable,
  onChange,
  getOptionValue,
  testID,
}: BMDropdownDesktopOptionsProps<T>) {
  const { noResultCaption, NoResultFooterComponent } =
    typeof searchable !== "boolean" && searchable
      ? searchable
      : { noResultCaption: undefined, NoResultFooterComponent: null };

  const { BMComponents, BMElevation } = useBMTheme();
  const styles = useMemo(
    () => createStyles(BMComponents, BMElevation, show),
    [BMComponents, BMElevation, show]
  );
  const { CorporateGeneralErrorMessage: cr } = useMobileContentResource();

  const [contentHeight, setContentHeight] = useState(0);
  const animatedHeight = useAnimatedDropdownHeight(show, contentHeight);
  const getValue = useGetValue(getOptionValue);

  const filteredOptions = useMemo(
    () =>
      option.items.filter((item) =>
        item.label.toLowerCase().includes(query.toLowerCase())
      ),
    [query, option.items]
  );

  const onLayout = useCallback((e: LayoutChangeEvent) => {
    setContentHeight(e.nativeEvent.layout.height);
  }, []);

  const handleOutsideClick = useCallback(
    (event: MouseEvent) => {
      if (
        wrapperRef.current instanceof HTMLElement &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        onChange(selected);
      }
    },
    [wrapperRef, onChange, selected]
  );

  const renderItem = useCallback(
    ({ item, index }: { item: BMDropdownOptionsValue<T>; index: number }) => {
      const isSelected = getValue(item.value) === getValue(selected?.value);
      return (
        <Pressable
          onPress={() => onChange(item)}
          testID={appendTestId(testID, `option-${index}`)}
        >
          <BMDropdownOptionItem
            {...item}
            query={query}
            isRadio={isRadio}
            isSelected={isSelected}
          />
        </Pressable>
      );
    },
    [getValue, selected, onChange, isRadio, query]
  );

  useEffect(() => {
    if (Platform.OS !== "web" || !show) return;
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      if (Platform.OS !== "web" || !show) return;
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [show, handleOutsideClick]);

  const renderContent = () => {
    if (option.isLoading) {
      return (
        <BMView style={styles.marginVerticalXXL}>
          <DotLoader />
        </BMView>
      );
    }

    if (option.isError) {
      return (
        <BMView justify="start" align="start">
          <ErrorSection
            type="SMALL"
            title={cr.perSectionTitle}
            caption={cr.perSectionDescription}
            button={[
              {
                type: "SECONDARY",
                label: cr.perSectionCTA,
                onPress: option.onClickRetry ?? (() => {}),
              },
              undefined,
            ]}
            style={styles.errorWrapperStyle}
          />
        </BMView>
      );
    }

    if (filteredOptions.length > 0) {
      return (
        <FlatList
          data={filteredOptions}
          renderItem={renderItem}
          showsVerticalScrollIndicator={false}
          ItemSeparatorComponent={() => <BMView style={styles.divider} />}
        />
      );
    }

    return (
      <BMDropdownNotFoundOptions
        query={query}
        noResultCaption={noResultCaption}
        NoResultFooterComponent={
          typeof NoResultFooterComponent === "function"
            ? NoResultFooterComponent
            : undefined
        }
      />
    );
  };

  return (
    <BMView style={styles.wrapper}>
      <BMView style={styles.container}>
        <Animated.View
          style={[styles.card, wrapperStyles, { height: animatedHeight }]}
        >
          <BMView onLayout={onLayout}>{renderContent()}</BMView>
        </Animated.View>
      </BMView>
    </BMView>
  );
}

function createStyles(
  BMComponents: BMComponentsType,
  BMElevation: BMElevationType,
  isOpen: boolean
) {
  return StyleSheet.create({
    wrapper: { position: "relative" },
    container: {
      zIndex: 99,
      height: "100%",
      width: "100%",
      position: "absolute",
      top: BMPrimitives.BMSize.xs,
    },
    card: {
      ...BMElevation.elevation2,
      ...(isOpen && {
        borderWidth: BMPrimitives.BMBorder.width.thick,
        borderColor: BMComponents.BMStyleCardBordercolor,
      }),
      overflow: "scroll",
      borderRadius: BMComponents.BMStyleCardRadiusCompact,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolor,
      maxHeight: MAX_HEIGHT_OPTIONS_CONTAINER,
    },
    divider: {
      borderBottomWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleCardBordercolor,
    },
    marginVerticalXXL: { marginVertical: BMPrimitives.BMSize.xl2 },
    errorWrapperStyle: {
      minWidth: "auto",
      marginTop: BMPrimitives.BMSize.xs,
      marginBottom: -BMPrimitives.BMSize.md,
      marginHorizontal: BMPrimitives.BMSize.md,
    },
  });
}
